import { useState } from "react";
import { Alert, Button, Group, List, Modal, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconSparkles, IconCheck, IconAlertTriangle, IconBrandSteam } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSession } from "../../auth/SessionContext";
import { DEBUG } from "../..";
import { steamLogin } from "../../auth/steamLogin";

const TRIAL_USED_KEY = "lmutv-trial-used";

export const hasUsedTrial = (): boolean => {
    // Dev: always show the banner so the demo flow stays reproducible across
    // reloads. Prod: real persistence via localStorage (and ultimately the
    // backend single-use enforcement).
    if (process.env.NODE_ENV === "development") return false;
    try { return localStorage.getItem(TRIAL_USED_KEY) === "true"; } catch { return false; }
};

interface TrialModalProps {
    opened: boolean;
    onClose: () => void;
}

async function requestTrialAsync(): Promise<{ ok: boolean; key?: string; error?: string }> {
    // Production endpoint — backend enforces single-use per Steam account
    // (both for requesting AND for redeeming the issued key).
    // TODO: implement /dyn/lmutv/request-trial.php server-side.
    try {
        const r = await fetch("/dyn/lmutv/request-trial.php", { method: "POST", credentials: "include" });
        const j = await r.json();
        return { ok: j.success === true, key: j.key, error: j.error };
    } catch (e: any) {
        if (DEBUG) console.error("requestTrial", e);
        return { ok: false, error: e?.message ?? "network" };
    }
}

export function TrialModal({ opened, onClose }: TrialModalProps) {
    const { t } = useTranslation("translation");
    const { sessionData } = useSession();
    const loggedIn = !!sessionData?.logged_in;
    const [submitting, setSubmitting] = useState(false);
    const [grantedKey, setGrantedKey] = useState<string | null>(null);

    const activate = async () => {
        if (!loggedIn) {
            steamLogin();
            return;
        }
        setSubmitting(true);
        const isDev = process.env.NODE_ENV === "development";
        const result = isDev
            ? { ok: true, key: "trial-pro-7d-demo-0000-0000-000000000000" }
            : await requestTrialAsync();
        setSubmitting(false);
        if (result.ok) {
            try { localStorage.setItem(TRIAL_USED_KEY, "true"); } catch {}
            setGrantedKey(result.key ?? "");
            // Let the /account OrderList show the freshly-granted key in dev.
            window.dispatchEvent(new CustomEvent("lmutv-trial-granted", { detail: { key: result.key } }));
        }
    };

    const handleClose = () => {
        setGrantedKey(null);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            size="md"
            centered
            title={
                <Group gap="xs">
                    <ThemeIcon variant="light" color="lmuTVColors" radius="xl">
                        <IconSparkles size={16} />
                    </ThemeIcon>
                    <Title order={4}>{t("trial.modal.title")}</Title>
                </Group>
            }
        >
            <Stack gap="md">
                {grantedKey ? (
                    <Stack gap="sm">
                        <Alert color="lmuTVColors" variant="light" icon={<IconCheck size={18} />}>
                            {t("trial.modal.granted")}
                        </Alert>
                        <Text size="sm" c="dimmed">{t("trial.modal.grantedHint")}</Text>
                        <Text size="xs" style={{ wordBreak: "break-all" }} c="lmuTVColors.4" fw={600}>
                            {grantedKey}
                        </Text>
                        <Button onClick={handleClose} fullWidth color="lmuTVColors">
                            {t("trial.modal.close")}
                        </Button>
                    </Stack>
                ) : (
                    <>
                        <Text size="sm">{t("trial.modal.lede")}</Text>
                        <List
                            spacing={6}
                            size="sm"
                            icon={
                                <ThemeIcon color="lmuTVColors" size={18} radius="xl">
                                    <IconCheck size={11} />
                                </ThemeIcon>
                            }
                        >
                            <List.Item>{t("trial.modal.bullets.tier")}</List.Item>
                            <List.Item>{t("trial.modal.bullets.duration")}</List.Item>
                            <List.Item>{t("trial.modal.bullets.pcs")}</List.Item>
                        </List>
                        <Alert
                            color="yellow"
                            variant="light"
                            icon={<IconAlertTriangle size={16} />}
                            title={t("trial.modal.policyTitle")}
                        >
                            {t("trial.modal.policyBody")}
                        </Alert>
                        <Button
                            onClick={activate}
                            loading={submitting}
                            color="lmuTVColors"
                            fullWidth
                            leftSection={loggedIn ? <IconSparkles size={16} /> : <IconBrandSteam size={16} />}
                        >
                            {loggedIn ? t("trial.modal.activate") : t("trial.modal.loginToActivate")}
                        </Button>
                    </>
                )}
            </Stack>
        </Modal>
    );
}
